<template>
  <div class="product-details-content-upload-bunny">
    <label class="mb-3">Vídeo da Aula</label>

    <!-- Loading durante auto-save -->
    <div
      v-if="isSavingDraft"
      class="alert alert-warning mb-4 d-flex align-items-center"
    >
      <i class="fa-regular fa-spinner-third fa-spin mr-2"></i>
      <strong>Salvando aula como rascunho...</strong>
    </div>

    <!-- Card de resumo do vídeo existente -->
    <div
      v-else-if="lesson.bunny_video && !showVideoReplacer"
      class="existing-video-summary mb-4"
      :class="getVideoStatusClass(lesson.bunny_video.status)"
    >
      <div class="video-summary-header">
        <div class="video-icon">
          <i :class="getVideoIconClass(lesson.bunny_video.status)"></i>
        </div>
        <div class="video-info">
          <h6 class="video-title">
            {{ lesson.bunny_video.title || lesson.name }}
          </h6>
          <div class="video-meta">
            <span :class="getStatusBadgeClass(lesson.bunny_video.status)">
              <i :class="getStatusIconClass(lesson.bunny_video.status)"></i>
              {{ getStatusText(lesson.bunny_video.status) }}
            </span>
            <span v-if="lesson.bunny_video.duration" class="duration">
              <i class="fa-regular fa-clock"></i>
              {{ formatVideoDuration(lesson.bunny_video.duration) }}
            </span>
          </div>
        </div>
      </div>

      <!-- Barra de progresso para vídeos em processamento -->
      <div
        v-if="['uploading', 'processing'].includes(lesson.bunny_video.status)"
        class="processing-bar mt-3"
      >
        <!-- <div class="progress">
          <div
            class="progress-bar progress-bar-striped progress-bar-animated"
            role="progressbar"
            :style="{ width: '100%' }"
          ></div>
        </div> -->
        <small class="text-muted mt-2 d-block">
          <i class="fa-regular fa-clock"></i>
          O vídeo está sendo processado pela Looma Videos. Isso pode levar
          alguns minutos.
        </small>
        <small class="text-muted mt-1 d-block" style="font-size: 11px">
          💡 <strong>Dica:</strong> Você pode sair desta página. O status será
          atualizado automaticamente quando o processamento terminar.
        </small>
      </div>

      <div class="video-actions">
        <a
          v-if="
            lesson.bunny_video.video_url &&
              lesson.bunny_video.status === 'ready'
          "
          :href="lesson.bunny_video.video_url"
          target="_blank"
          class="btn btn-sm btn-outline-primary"
        >
          <i class="fa-regular fa-play"></i>
          Assistir Vídeo
        </a>
        <button
          v-if="
            lesson.bunny_video.embed_url &&
              lesson.bunny_video.status === 'ready'
          "
          @click="copyEmbedUrl(lesson.bunny_video.embed_url)"
          class="btn btn-sm btn-outline-secondary"
        >
          <i class="fa-regular fa-copy"></i>
          Copiar Embed
        </button>
        <button
          v-if="lesson.bunny_video.status === 'ready'"
          @click="showVideoReplacer = true"
          class="btn btn-sm btn-outline-warning"
        >
          <i class="fa-regular fa-arrow-rotate-right"></i>
          Substituir Vídeo
        </button>
        <button
          v-if="['uploading', 'processing'].includes(lesson.bunny_video.status)"
          @click="refreshVideoStatus"
          class="btn btn-sm btn-outline-info"
        >
          <i class="fa-regular fa-arrows-rotate"></i>
          Atualizar Status
        </button>
        <button
          @click="confirmRemoveVideo"
          class="btn btn-sm btn-outline-danger"
        >
          <i class="fa-regular fa-trash"></i>
          Remover Vídeo
        </button>
      </div>
    </div>

    <!-- Uploader (quando tem ID e não tem vídeo ou quer substituir) -->
    <video-uploader
      v-else-if="lesson.id"
      ref="videoUploader"
      :lesson-id="lesson.id"
      :lesson-title="lesson.name || lesson.title"
      :existing-video="lesson.bunny_video"
      @success="onVideoUploadSuccess"
      @error="onVideoUploadError"
    />

    <!-- Aguardando ID da aula -->
    <div v-else class="alert alert-info mb-4">
      <i class="fa-regular fa-circle-info"></i>
      Preencha o nome da aula e clique em "Upload para Looma Videos" para
      começar.
    </div>

    <small class="small-text text-secondary mt-3 d-block">
      Faça upload direto para Looma Videos. Seus vídeos serão hospedados,
      processados e entregues com alta performance. Suporta HLS e funciona
      perfeitamente em todos os dispositivos, incluindo mobile.
    </small>

    <!-- Modal de confirmação de remoção -->
    <responsive-modal v-model="visible.remove_video_modal" size="md">
      <div class="confirmation p-5">
        <div class="text-center mb-4">
          <div class="icon-warning mb-3">
            <i class="fa-regular fa-triangle-exclamation"></i>
          </div>
          <h4 class="mb-3">Remover Vídeo?</h4>
          <p class="text-muted">
            Tem certeza que deseja remover este vídeo da aula?
          </p>
          <p class="text-muted mb-0">
            <strong>Nota:</strong> O vídeo será desassociado desta aula, mas não
            será excluído permanentemente.
          </p>
        </div>

        <div class="d-flex justify-content-center gap-3">
          <button
            class="btn btn-secondary btn-40"
            type="button"
            @click="visible.remove_video_modal = false"
          >
            Cancelar
          </button>
          <button
            class="btn btn-danger btn-40"
            type="button"
            @click="handleRemoveVideo"
          >
            <i class="fa-regular fa-trash"></i>
            Sim, Remover
          </button>
        </div>
      </div>
    </responsive-modal>
  </div>
</template>

<script>
import VideoUploader from "@/components/product/VideoUploader.vue";

export default {
  components: {
    VideoUploader
  },
  props: {
    isSavingDraft: Boolean,
    lesson: Object
  },
  data() {
    return {
      visible: {
        remove_video_modal: false
      },
      showVideoReplacer: false // Controla se mostra uploader ao substituir vídeo
    };
  },
  beforeDestroy() {
    this.stopVideoPolling();
  },
  watch: {
    "lesson.bunny_video": {
      handler(bunnyVideo) {
        if (bunnyVideo && bunnyVideo.id) {
          this.useVideoUpload = true;
        }
      },
      immediate: true
    },
    "lesson.bunny_video.status": {
      handler(newStatus) {
        if (["uploading", "processing"].includes(newStatus)) {
          this.startVideoPolling();
        } else {
          this.stopVideoPolling();
        }
      },
      immediate: true
    }
  },
  methods: {
    getVideoStatusClass(status) {
      const classes = {
        ready: "status-ready",
        uploading: "status-processing",
        processing: "status-processing",
        failed: "status-error",
        cancelled: "status-error"
      };
      return classes[status] || "";
    },
    getVideoIconClass(status) {
      const icons = {
        ready: "fa-solid fa-circle-play",
        uploading: "fa-solid fa-cloud-arrow-up",
        processing: "fa-solid fa-gear fa-spin",
        failed: "fa-solid fa-circle-exclamation",
        cancelled: "fa-solid fa-circle-xmark"
      };
      return icons[status] || "fa-solid fa-video";
    },
    getStatusBadgeClass(status) {
      const classes = {
        ready: "badge-ready",
        uploading: "badge-processing",
        processing: "badge-processing",
        failed: "badge-error",
        cancelled: "badge-error"
      };
      return classes[status] || "badge-ready";
    },
    getStatusIconClass(status) {
      const icons = {
        ready: "fa-regular fa-circle-check",
        uploading: "fa-regular fa-spinner-third fa-spin",
        processing: "fa-regular fa-spinner-third fa-spin",
        failed: "fa-regular fa-circle-exclamation",
        cancelled: "fa-regular fa-circle-xmark"
      };
      return icons[status] || "fa-regular fa-circle-check";
    },
    getStatusText(status) {
      const texts = {
        ready: "Vídeo pronto",
        uploading: "Enviando...",
        processing: "Processando...",
        failed: "Erro no processamento",
        cancelled: "Cancelado"
      };
      return texts[status] || status;
    },
    formatVideoDuration(seconds) {
      if (!seconds) return "0s";
      const hours = Math.floor(seconds / 3600);
      const minutes = Math.floor((seconds % 3600) / 60);
      const secs = seconds % 60;

      if (hours > 0) {
        return `${hours}h ${minutes}m`;
      } else if (minutes > 0) {
        return `${minutes}m ${secs}s`;
      }
      return `${secs}s`;
    },
    async copyEmbedUrl(url) {
      try {
        await navigator.clipboard.writeText(url);
        this.$message.success("URL copiada para a área de transferência!");
      } catch (err) {
        this.$message.error("Erro ao copiar URL");
      }
    },
    onVideoUploadSuccess(result) {
      this.showVideoReplacer = false;

      const lessonTitle = this.lesson?.name || this.lesson?.title || "Vídeo";
      this.$message.success(
        `Vídeo "${lessonTitle}" foi adicionado com sucesso!`
      );

      if (this.lesson && result) {
        this.$emit("video-upload-success", {
          id: result.id,
          title:
            this.lesson.name || this.lesson.title || result.title || "Vídeo",
          status: result.status || "uploading",
          duration: result.duration,
          video_url: result.videoUrl,
          embed_url: result.embedUrl,
          bunny_video_id: result.bunnyVideoId
        });
      }
    },
    onVideoUploadError(error) {
      this.$message.error(`Erro ao fazer upload: ${error.message}`);
    },
    async refreshVideoStatus(showMessage = true) {
      if (!this.lesson || !this.lesson.id) {
        return;
      }

      try {
        if (showMessage) {
          this.$message.info("Atualizando status do vídeo...");
        }

        const response = await window.axios.get(
          `/bunny-videos/videos/?lesson_id=${this.lesson.id}`,
          {
            baseURL: membersBaseURL()
          }
        );

        if (
          response.data &&
          response.data.results &&
          response.data.results.length > 0
        ) {
          const video = response.data.results[0];
          const oldStatus = this.lesson.bunny_video?.status;

          this.$emit("video-status-update", video);

          if (showMessage) {
            if (video.status === "ready" && oldStatus !== "ready") {
              this.$message.success("Vídeo processado com sucesso!");
              this.stopVideoPolling();
            } else if (video.status === "failed") {
              this.$message.error("Erro no processamento do vídeo");
              this.stopVideoPolling();
            } else if (["uploading", "processing"].includes(video.status)) {
              this.$message.warning(
                "Vídeo ainda está sendo processado. Aguarde mais um pouco."
              );
            } else {
              this.$message.success("Status atualizado!");
            }
          } else {
            if (video.status === "ready" || video.status === "failed") {
              this.stopVideoPolling();
              if (video.status === "ready") {
                this.$message.success("Vídeo processado com sucesso!");
              }
            }
          }
        }
      } catch (error) {
        if (showMessage) {
          this.$message.error("Erro ao atualizar status do vídeo");
        }
      }
    },
    startVideoPolling() {
      if (this.videoPollingInterval) {
        return;
      }
      this.videoPollingInterval = setInterval(() => {
        this.refreshVideoStatus(false);
      }, 15000);
    },
    stopVideoPolling() {
      if (this.videoPollingInterval) {
        clearInterval(this.videoPollingInterval);
        this.videoPollingInterval = null;
      }
    },
    confirmRemoveVideo() {
      this.visible.remove_video_modal = true;
    },
    async handleRemoveVideo() {
      if (!this.lesson.bunny_video || !this.lesson.bunny_video.id) {
        this.$message.error("Nenhum vídeo para remover");
        return;
      }

      try {
        await this.videoUploadService.removeFromLesson(
          this.lesson.bunny_video.id
        );

        this.$emit("video-status-update", null);

        if (
          this.form.link_media &&
          (this.form.link_media.includes("b-cdn.net") ||
            this.form.link_media.includes("mediadelivery.net"))
        ) {
          this.form.link_media = "";
        }

        this.visible.remove_video_modal = false;

        this.$message.success("Vídeo removido com sucesso!");
      } catch (error) {
        this.$message.error(error.message || "Erro ao remover vídeo");
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.product-details-content-upload-bunny {
  .alert {
    padding: 12px 16px;
    border-radius: 8px;
    display: flex;
    align-items: flex-start;
    gap: 12px;
    font-size: 14px;

    &.alert-info {
      background-color: #d1ecf1;
      border: 1px solid #bee5eb;
      color: #0c5460;

      i {
        font-size: 20px;
        color: #0c5460;
        margin-top: 2px;
      }

      strong {
        font-weight: 600;
      }
    }
  }

  .existing-video-summary {
    border: 2px solid #28a745;
    border-radius: 12px;
    padding: 20px;
    background: linear-gradient(135deg, #f8fff9 0%, #e8f5e9 100%);

    &.status-processing {
      border-color: #ffc107;
      background: linear-gradient(135deg, #fffbf0 0%, #fff3cd 100%);

      .video-icon {
        background: linear-gradient(135deg, #ffc107, #ff9800);
      }

      .video-title {
        color: #856404;
      }

      .video-actions {
        border-top-color: #ffeeba;
      }
    }

    &.status-error {
      border-color: #dc3545;
      background: linear-gradient(135deg, #fff5f5 0%, #f8d7da 100%);

      .video-icon {
        background: linear-gradient(135deg, #dc3545, #c82333);
      }

      .video-title {
        color: #721c24;
      }

      .video-actions {
        border-top-color: #f5c6cb;
      }
    }

    .video-summary-header {
      display: flex;
      align-items: center;
      gap: 16px;
      margin-bottom: 16px;

      .video-icon {
        width: 56px;
        height: 56px;
        background: linear-gradient(135deg, #28a745, #20c997);
        border-radius: 12px;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;

        i {
          font-size: 28px;
          color: white;
        }
      }

      .video-info {
        flex: 1;
        min-width: 0;

        .video-title {
          font-size: 16px;
          font-weight: 600;
          color: #155724;
          margin: 0 0 8px 0;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .video-meta {
          display: flex;
          align-items: center;
          gap: 12px;
          flex-wrap: wrap;

          .badge-ready,
          .badge-processing,
          .badge-error {
            display: inline-flex;
            align-items: center;
            gap: 6px;
            padding: 4px 10px;
            border-radius: 12px;
            font-size: 12px;
            font-weight: 500;

            i {
              font-size: 12px;
            }
          }

          .badge-ready {
            background-color: #d4edda;
            color: #155724;
          }

          .badge-processing {
            background-color: #fff3cd;
            color: #856404;
          }

          .badge-error {
            background-color: #f8d7da;
            color: #721c24;
          }

          .duration {
            display: inline-flex;
            align-items: center;
            gap: 6px;
            color: #495057;
            font-size: 13px;

            i {
              font-size: 12px;
            }
          }
        }
      }
    }

    .video-actions {
      display: flex;
      gap: 8px;
      flex-wrap: wrap;
      padding-top: 12px;
      border-top: 1px solid #c3e6cb;

      .btn {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        font-size: 13px;
        padding: 8px 14px;

        i {
          font-size: 14px;
        }
      }
    }

    .processing-bar {
      .progress {
        height: 8px;
        background-color: rgba(0, 0, 0, 0.1);
        border-radius: 4px;
        overflow: hidden;

        .progress-bar {
          background: linear-gradient(90deg, #ffc107, #ff9800);
        }
      }

      small {
        font-size: 12px;

        i {
          font-size: 12px;
        }
      }
    }
  }
}
</style>
